import { trackEvent } from '@/lib/trackEvent';
import {
  getReportOpenedAt,
  getScrollDepthPercent,
} from '@/lib/reportScrolled';
import {
  getMostVisibleSection,
  getPrimaryVisibleSection,
} from '@/lib/reportSectionVisibility';

const HEARTBEAT_INTERVAL_MS = 30_000;
const SAMPLING_THRESHOLD = 50_000;
const SAMPLING_RATE = 0.25;
const SAMPLE_KEY_PREFIX = 'ph_heartbeat_sample:';

type HeartbeatSampling = {
  shouldTrack: boolean;
  isSampled: boolean;
};

const getSampleKey = (reportId: string) => `${SAMPLE_KEY_PREFIX}${reportId}`;

export const resolveHeartbeatSampling = (
  reportId: string,
  editionSessionCount = 0
): HeartbeatSampling => {
  if (editionSessionCount < SAMPLING_THRESHOLD) {
    return { shouldTrack: true, isSampled: false };
  }

  if (typeof sessionStorage === 'undefined') {
    const shouldTrack = Math.random() < SAMPLING_RATE;
    return { shouldTrack, isSampled: shouldTrack };
  }

  const existing = sessionStorage.getItem(getSampleKey(reportId));
  if (existing === '1') return { shouldTrack: true, isSampled: true };
  if (existing === '0') return { shouldTrack: false, isSampled: false };

  const shouldTrack = Math.random() < SAMPLING_RATE;
  sessionStorage.setItem(getSampleKey(reportId), shouldTrack ? '1' : '0');
  return { shouldTrack, isSampled: shouldTrack };
};

export const startReportAttentionTracking = (
  reportId: string,
  options: {
    root?: ParentNode;
    editionSessionCount?: number;
  } = {}
): (() => void) => {
  if (!reportId || typeof window === 'undefined') {
    return () => undefined;
  }

  const root = options.root ?? document;
  const sampling = resolveHeartbeatSampling(
    reportId,
    options.editionSessionCount ?? 0
  );

  let heartbeatIndex = 0;
  let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  let currentSectionName: string | null = null;
  let sectionEnteredAt = Date.now();
  let locationObserver: IntersectionObserver | null = null;

  const getElapsedSeconds = () =>
    Math.max(0, Math.floor((Date.now() - getReportOpenedAt(reportId)) / 1000));

  const emitHeartbeat = () => {
    if (!sampling.shouldTrack) return;
    if (document.hidden) return;

    heartbeatIndex += 1;
    const visibleSection = getMostVisibleSection(root);

    const properties: Record<string, unknown> = {
      visible_section: visibleSection?.name ?? null,
      scroll_depth_percent: getScrollDepthPercent(),
      elapsed_seconds: getElapsedSeconds(),
      heartbeat_index: heartbeatIndex,
    };

    if (sampling.isSampled) {
      properties.is_sampled = true;
    }

    trackEvent('report_active_heartbeat', properties);
  };

  const startHeartbeat = () => {
    if (!sampling.shouldTrack || heartbeatTimer != null) return;
    if (document.hidden) return;
    heartbeatTimer = setInterval(emitHeartbeat, HEARTBEAT_INTERVAL_MS);
  };

  const stopHeartbeat = () => {
    if (heartbeatTimer == null) return;
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  };

  const emitLocationChange = (nextSectionName: string | null) => {
    if (!nextSectionName) return;
    if (nextSectionName === currentSectionName) return;

    const previousSection = currentSectionName;
    const timeInPreviousMs =
      previousSection != null ? Math.max(0, Date.now() - sectionEnteredAt) : 0;

    if (previousSection != null) {
      trackEvent('report_active_location', {
        section_name: nextSectionName,
        previous_section: previousSection,
        time_in_previous_ms: timeInPreviousMs,
      });
    }

    currentSectionName = nextSectionName;
    sectionEnteredAt = Date.now();
  };

  const syncPrimarySection = () => {
    const primary = getPrimaryVisibleSection(root, 0.5);
    if (!primary) return;

    if (currentSectionName == null) {
      currentSectionName = primary.name;
      sectionEnteredAt = Date.now();
      return;
    }

    emitLocationChange(primary.name);
  };

  const handleVisibilityChange = () => {
    if (document.hidden) {
      stopHeartbeat();
      return;
    }
    startHeartbeat();
    syncPrimarySection();
  };

  if (typeof IntersectionObserver !== 'undefined') {
    locationObserver = new IntersectionObserver(
      () => {
        syncPrimarySection();
      },
      { threshold: [0, 0.25, 0.5, 0.75, 1] }
    );

    root.querySelectorAll('[data-report-section]').forEach((element) => {
      locationObserver?.observe(element);
    });
  }

  syncPrimarySection();
  startHeartbeat();
  document.addEventListener('visibilitychange', handleVisibilityChange);

  return () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    stopHeartbeat();
    locationObserver?.disconnect();
  };
};
